/**
 * Bausteine der Verwaltung („Werkbank", Entwurf B): Seitenkopf mit Pfad, Kennzahlen (auf Wunsch zugleich Schnellfilter),
 * klebende Aktionsleiste, Verteilungsbalken und Suchfeld. Gestaltet in `admin.css` (`.ksb-seitenkopf`, `.ksb-kennzahl*`,
 * `.ksb-aktionsleiste`, `.ksb-verteilung*`, `.ksb-suche`). **Farbe trägt nie allein die Aussage:** Kennzahl und
 * Verteilung zeigen immer Wort und Zahl, der Balken ist schmückend.
 */

import type { CSSProperties, InputHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router';

import { CaretRight, MagnifyingGlass } from '../icons';
import { classNames } from './classNames';

export interface PfadEintrag {
  text: string;
  /** Fehlt beim letzten Eintrag (der aktuellen Seite). */
  to?: string;
}

export interface SeitenkopfProps {
  /** Brotkrumen über dem Titel, z. B. Verwaltung › Termine. */
  pfad?: PfadEintrag[];
  titel: ReactNode;
  untertitel?: ReactNode;
  /** Hauptaktionen der Seite, rechts. */
  aktionen?: ReactNode;
  /** `id` der Überschrift, für `aria-labelledby` der Seite. */
  titelId?: string;
}

/** Kopf einer Seite: Pfad, Titel (die einzige `h1` der Seite), Untertitel und Hauptaktionen. */
export function Seitenkopf({ pfad, titel, untertitel, aktionen, titelId }: SeitenkopfProps) {
  return (
    <header className="ksb-seitenkopf">
      <div className="ksb-seitenkopf__text">
        {pfad && pfad.length > 0 && (
          <nav className="ksb-pfad" aria-label="Pfad">
            <ol>
              {pfad.map((p, i) => (
                <li key={p.text}>
                  {i > 0 && <CaretRight aria-hidden="true" />}
                  {p.to ? (
                    <Link to={p.to}>{p.text}</Link>
                  ) : (
                    <span aria-current="page">{p.text}</span>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        )}
        <h1 className="ksb-h1" {...(titelId && { id: titelId })}>
          {titel}
        </h1>
        {untertitel !== undefined && <p className="ksb-seitenkopf__sub">{untertitel}</p>}
      </div>
      {aktionen !== undefined && <div className="ksb-seitenkopf__aktionen">{aktionen}</div>}
    </header>
  );
}

/** Reihe von Kennzahlen (vier Spalten, auf schmalen Fenstern zwei). */
export function Kennzahlen({
  beschriftung,
  children,
}: {
  beschriftung: string;
  children: ReactNode;
}) {
  return (
    <div className="ksb-kennzahlen" role="group" aria-label={beschriftung}>
      {children}
    </div>
  );
}

export interface KennzahlProps {
  icon?: ReactNode;
  label: string;
  wert: number | string;
  /** Gesamtmenge hinter dem Wert („118 von 266“). */
  von?: number | string;
  /** Erklärung unter der Zahl. */
  text?: ReactNode;
  /** Anteil 0…1 als schmaler Balken unter der Zahl (schmückend: Zahl und Text tragen die Aussage). */
  anteil?: number;
  /** Mit `onClick` wird die Kennzahl zum Schalter (Schnellfilter) und zeigt `gewaehlt` als `aria-pressed`. */
  onClick?: () => void;
  gewaehlt?: boolean;
}

export function Kennzahl({
  icon,
  label,
  wert,
  von,
  text,
  anteil,
  onClick,
  gewaehlt = false,
}: KennzahlProps) {
  const inhalt = (
    <>
      <span className="ksb-kennzahl__label">
        {icon}
        {label}
      </span>
      <span className="ksb-kennzahl__wert">
        {wert}
        {von !== undefined && <span className="ksb-kennzahl__von"> von {von}</span>}
      </span>
      {anteil !== undefined && (
        <span className="ksb-kennzahl__bahn" aria-hidden="true">
          <span style={{ inlineSize: `${Math.min(1, Math.max(0, anteil)) * 100}%` }} />
        </span>
      )}
      {text !== undefined && <span className="ksb-kennzahl__text">{text}</span>}
    </>
  );
  return onClick ? (
    <button type="button" className="ksb-kennzahl" aria-pressed={gewaehlt} onClick={onClick}>
      {inhalt}
    </button>
  ) : (
    <div className="ksb-kennzahl">{inhalt}</div>
  );
}

/**
 * Leiste am unteren Rand, die beim Scrollen stehen bleibt: Aktionen für eine Auswahl oder ungespeicherte Änderungen.
 * Als klebende Leiste darf sie einen Schatten tragen (Paket). Die Schaltflächen darin sind `contrast="negative"`.
 */
export function Aktionsleiste({
  beschriftung,
  children,
}: {
  beschriftung: string;
  children: ReactNode;
}) {
  return (
    <div className="ksb-aktionsleiste ksb-werkzeug" role="region" aria-label={beschriftung}>
      {children}
    </div>
  );
}

export interface VerteilungTeil {
  text: string;
  wert: number;
  /** Farbe des Teils im Balken und des Punkts in der Legende — ein Token (`var(--color-…)`). */
  farbe: string;
}

/** Ein Balken in Teilen mit Legende. Teile mit Wert 0 fehlen im Balken, die Legende nennt sie trotzdem. */
export function Verteilung({ teile }: { teile: VerteilungTeil[] }) {
  return (
    <div className="ksb-verteilung">
      <div className="ksb-verteilung__balken" aria-hidden="true">
        {teile
          .filter((t) => t.wert > 0)
          .map((t) => (
            <span key={t.text} style={{ flex: t.wert, '--c': t.farbe } as CSSProperties} />
          ))}
      </div>
      <ul className="ksb-verteilung__legende">
        {teile.map((t) => (
          <li key={t.text}>
            <span
              className="ksb-dot"
              style={{ '--c': t.farbe } as CSSProperties}
              aria-hidden="true"
            />
            {t.text}
            <b>{t.wert}</b>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Suchfeld mit Lupe; `aria-label` bleibt Pflicht, weil keine sichtbare Beschriftung dabei steht. */
export function Suchfeld({
  className,
  breit = false,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'aria-label'> & {
  'aria-label': string;
  /** Bis 440 statt 280 px breit (für Felder allein auf einer Zeile). */
  breit?: boolean;
}) {
  return (
    <span className={classNames('ksb-suche', breit && 'ksb-suche--breit', className)}>
      <MagnifyingGlass aria-hidden="true" />
      <input type="search" className="ksb-txt ksb-txt--fill" {...rest} />
    </span>
  );
}
